"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RestService = void 0;
var axios_1 = __importDefault(require("axios"));
var react_router_dom_1 = require("react-router-dom");
var auth_service_1 = require("./auth.service");
var consumer_services_media_types_config_1 = require("../configs/consumer-services-media-types.config");
var enums_1 = require("../shared/enums");
/**
 * Class declaration
 */
var RestService = /** @class */ (function () {
    /**
     * constructor
     *
     * @param restServiceProps
     */
    function RestService(restServiceProps) {
        var _a;
        // Set default configuration
        this.defaultRequestConfig = {
            baseURL: restServiceProps.baseURL,
        };
        // Set the service name
        this.serviceName = restServiceProps.serviceName;
        // Initialize the client
        this.client = this.createClient();
        // Create a _authService object
        this.authService = (_a = restServiceProps.authService) !== null && _a !== void 0 ? _a : new auth_service_1.AuthService();
    }
    /**
     *
     * @private createClient()
     */
    RestService.prototype.createClient = function () {
        return axios_1.default.create(this.defaultRequestConfig);
    };
    /**
     * headers
     *
     * @param method
     * @param useAuthentication
     * @param resourceType
     */
    RestService.prototype.headers = function (method, useAuthentication, resourceType, overrideHeaders) {
        if (method === void 0) { method = enums_1.RequestMethodEnum.GET; }
        if (useAuthentication === void 0) { useAuthentication = true; }
        if (resourceType === void 0) { resourceType = ''; }
        var requestHeaders = {};
        // Set the Accept/Content-Type headers
        var accept = enums_1.MediaTypeEnum.APPLICATION_JSON;
        var contentType = enums_1.MediaTypeEnum.APPLICATION_JSON;
        if (this.serviceName !== undefined) {
            // Set to custom Media Types
            var mediaTypeKey = this.mediaTypeResolver(this.serviceName, resourceType);
            if (mediaTypeKey) {
                accept = mediaTypeKey;
                contentType = mediaTypeKey;
            }
        }
        // add 'Authorization' header if applicable
        if (useAuthentication && this.authService.isAuthenticated()) {
            requestHeaders['Authorization'] = 'Bearer ' + this.authService.getToken();
        }
        // set an 'Accept' header on all methods besides delete
        if (method !== enums_1.RequestMethodEnum.DELETE) {
            requestHeaders['Accept'] = accept;
        }
        // set a 'Content-Type' header on put, post and patch methods
        if (method === enums_1.RequestMethodEnum.PUT ||
            method === enums_1.RequestMethodEnum.POST ||
            method === enums_1.RequestMethodEnum.PATCH) {
            requestHeaders['Content-Type'] = contentType;
        }
        return __assign(__assign({}, requestHeaders), overrideHeaders);
    };
    RestService.prototype.mediaTypeResolver = function (serviceName, resourceType) {
        var matchedKey = Object.keys(consumer_services_media_types_config_1.consumerServicesMediaTypes[serviceName]).find(function (path) {
            return !!(0, react_router_dom_1.matchPath)({ path: "/".concat(path, "/").replaceAll('//', '/') }, "/".concat(resourceType, "/").replaceAll('//', '/').toLowerCase());
        });
        if (matchedKey) {
            return consumer_services_media_types_config_1.consumerServicesMediaTypes[serviceName][matchedKey];
        }
        return undefined;
    };
    /**
     * request
     *
     * @param resource
     * @param method
     * @param body
     * @param params
     * @param useAuthentication
     * @param headers
     */
    RestService.prototype.request = function (resource, method, 
    // Axios takes 'any' for the body type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    body, 
    // Axios takes 'any' for params
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params, useAuthentication, overrideHeaders, headers) {
        if (method === void 0) { method = enums_1.RequestMethodEnum.GET; }
        if (body === void 0) { body = undefined; }
        if (params === void 0) { params = undefined; }
        if (useAuthentication === void 0) { useAuthentication = true; }
        var requestConfig = __assign(__assign({}, this.defaultRequestConfig), { method: method, url: resource });
        // Set the params
        if (params !== undefined) {
            requestConfig['params'] = params;
        }
        // Set Headers
        if (!headers) {
            headers = this.headers(method, useAuthentication, resource, overrideHeaders);
        }
        requestConfig['headers'] = headers;
        // Set the body
        if (body !== undefined &&
            (method === enums_1.RequestMethodEnum.POST ||
                method === enums_1.RequestMethodEnum.PATCH ||
                method === enums_1.RequestMethodEnum.PUT ||
                method === enums_1.RequestMethodEnum.DELETE)) {
            requestConfig['data'] = body;
        }
        // Make the api call
        return this.client.request(requestConfig);
    };
    /**
     * search - call request() to GET a collection
     * @param resource
     * @param params
     * @param useAuthentication
     */
    RestService.prototype.search = function (resource, 
    // Axios takes 'any' for params
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params, useAuthentication) {
        if (params === void 0) { params = undefined; }
        if (useAuthentication === void 0) { useAuthentication = true; }
        return this.request(resource, enums_1.RequestMethodEnum.GET, undefined, params, useAuthentication);
    };
    /**
     * show - Call request() to GET a single resource specified by resource and id
     * @param resource
     * @param id
     * @param useAuthentication
     */
    RestService.prototype.show = function (resource, id, useAuthentication) {
        if (useAuthentication === void 0) { useAuthentication = true; }
        var path = resource + '/' + id;
        return this.request(path, enums_1.RequestMethodEnum.GET, undefined, undefined, useAuthentication);
    };
    /**
     * update - Call request() to perform a PUT if a defined ID is supplied, or a POST otherwise
     * @param resource
     * @param body
     * @param id
     * @param useAuthentication
     */
    RestService.prototype.update = function (resource, 
    // Axios takes 'any' for the body type
    body, id, useAuthentication, params) {
        if (id === void 0) { id = undefined; }
        if (useAuthentication === void 0) { useAuthentication = true; }
        if (params === void 0) { params = undefined; }
        var path = resource;
        var method = enums_1.RequestMethodEnum.POST;
        // do a PUT, if we have an id
        if (id !== undefined) {
            path = resource + '/' + id;
            method = enums_1.RequestMethodEnum.PUT;
        }
        return this.request(path, method, body, params, useAuthentication);
    };
    /**
     * delete() - Call request() to perform a DELETE operation
     * @param resource
     * @param id
     * @param useAuthentication
     */
    RestService.prototype.delete = function (resource, id, useAuthentication) {
        if (id === void 0) { id = undefined; }
        if (useAuthentication === void 0) { useAuthentication = true; }
        var path = resource;
        if (id !== undefined) {
            path = path + '/' + id;
        }
        return this.request(path, enums_1.RequestMethodEnum.DELETE, undefined, undefined, useAuthentication);
    };
    return RestService;
}());
exports.RestService = RestService;
